import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import { motion } from 'motion/react';
import { t } from '../i18n/index.ts';
import type { AmountUnit } from '../lib/amounts.ts';

export interface TabItem {
  key: string;
  label: ReactNode;
  /** Navigation tab (renders a link) … */
  to?: string;
  /** … or an in-page tab (renders a button with role="tab"). */
  onSelect?: () => void;
}

const underlineSpring = { type: 'spring', stiffness: 520, damping: 42, mass: 0.6 } as const;

/**
 * Tabs with an underline that slides to the active tab. Link tabs are a <nav> with
 * aria-current; button tabs form a tablist with aria-selected.
 */
export function TabBar({ items, active, group, label, fill }: { items: TabItem[]; active: string; group: string; label: string; fill?: boolean }) {
  const isNav = items.some((i) => i.to);
  const content = items.map((item) => {
    const selected = item.key === active;
    const underline = selected && <motion.span layoutId={`tab-underline-${group}`} className="tab-underline" transition={underlineSpring} aria-hidden="true" />;
    return item.to ? (
      <Link key={item.key} to={item.to} aria-current={selected ? 'page' : undefined}>
        {item.label}
        {underline}
      </Link>
    ) : (
      <button key={item.key} type="button" role="tab" aria-selected={selected} tabIndex={selected ? 0 : -1} onClick={item.onSelect}>
        {item.label}
        {underline}
      </button>
    );
  });
  const cls = `tabs ${fill ? 'tabs-fill' : ''}`;
  return isNav ? (
    <nav className={cls} aria-label={label} data-group={group}>
      {content}
    </nav>
  ) : (
    <div
      className={cls}
      data-group={group}
      role="tablist"
      aria-label={label}
      onKeyDown={(e) => {
        // Arrow keys move between tabs (and select them), Home/End jump to the ends.
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(e.key)) return;
        const tabs = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')];
        const i = tabs.indexOf(document.activeElement as HTMLButtonElement);
        if (i < 0) return;
        e.preventDefault();
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next]!.focus();
        tabs[next]!.click();
      }}
    >
      {content}
    </div>
  );
}

export function UnitToggle({ unit, onChange }: { unit: AmountUnit; onChange: (u: AmountUnit) => void }) {
  // Several toggles can be mounted at once (e.g. full-screen table): each needs its own layout id.
  const id = useId();
  return (
    <div className="unit-toggle" role="radiogroup" aria-label={t('table.unitLabel')}>
      {(['chips', 'bb'] as const).map((u) => (
        <button key={u} type="button" role="radio" aria-checked={unit === u} onClick={() => unit !== u && onChange(u)}>
          {unit === u && <motion.span layoutId={`unit-toggle-thumb-${id}`} className="unit-thumb" transition={underlineSpring} aria-hidden="true" />}
          <span className="unit-label">{u === 'chips' ? t('table.unitChips') : t('table.unitBb')}</span>
        </button>
      ))}
    </div>
  );
}
